import { useEffect, useState } from 'react';

import { RequestGatewayContext } from '../context/RequestGatewayContext';
import { TokenStorage } from '../commonTypes';
import { useTokensStore } from '../stores/useTokensStore';

export type RequestGatewayProps = {
  isProtectedRoute?: boolean;
  version?: string;
  source?: string;
  baseUrl: string;
  tokenStorage: TokenStorage;
  refreshTokenUrl: string;
};

export const RequestGateway = ({
  children,
  isProtectedRoute = false,
  baseUrl,
  version,
  source,
  tokenStorage,
  refreshTokenUrl,
}: React.PropsWithChildren<RequestGatewayProps>) => {
  const [tokensLoading, setTokensLoading] = useState<boolean>(true);
  useEffect(() => {
    // Manual subscribe to the store to avoid rendering issue
    const storeSubscription = useTokensStore.subscribe(
      async (state, prevState) => {
        if (state.accessToken !== prevState.accessToken) {
          if (
            !state.accessToken ||
            !state.refreshToken ||
            !state.accessTokenExpiration ||
            !state.refreshTokenExpiration
          ) {
            tokenStorage.clear();

            return;
          }

          // try to read tokens from storage
          const accessToken = await tokenStorage.readAccessToken();
          const refreshToken = await tokenStorage.readRefreshToken();

          if (
            state.accessToken !== accessToken?.token ||
            state.refreshToken !== refreshToken?.token
          ) {
            tokenStorage.writeAccessToken(
              state.accessToken,
              state.accessTokenExpiration
            );

            tokenStorage.writeRefreshToken(
              state.refreshToken,
              state.refreshTokenExpiration
            );
          }

          setTokensLoading(false);
        }
      }
    );

    const readTokensFromStore = async () => {
      // initial load of tokens from storage
      const accessToken = await tokenStorage.readAccessToken();
      const refreshToken = await tokenStorage.readRefreshToken();

      if (accessToken?.token && refreshToken?.token) {
        useTokensStore.setState({
          accessToken: accessToken.token,
          refreshToken: refreshToken.token,
          accessTokenExpiration: accessToken.expiresAt,
          refreshTokenExpiration: refreshToken.expiresAt,
        });
      }

      setTokensLoading(false);
    };

    readTokensFromStore();

    return storeSubscription;
  }, [tokenStorage]);

  if (tokensLoading) return null;

  return (
    <RequestGatewayContext.Provider
      value={{
        isProtectedRoute,
        baseUrl,
        version,
        source,
        tokenStorage,
        refreshTokenUrl,
      }}
    >
      {children}
    </RequestGatewayContext.Provider>
  );
};
